import moment from "moment-timezone";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { isBooked } from "../BusinessUtils";
import { getCartItemPrice, getProductTitle } from "../Helper";
import { agesSelector, cartCreatedFromReservationSelector } from "../selectors/Selectors";
import { sendSignal, signal } from "../signals";
import { SIGNAL_CART_UPDATED } from "../signals/types";
import { TCartItem, TGeneralAges } from "../store/types";
import { useAppSelector } from "./useAppSelector";
import { useCartPriceCalculation } from "./useCartPriceCalculation";

export const useSendCartSignalOnUpdate = () => {
    const isReservationModification = useAppSelector(cartCreatedFromReservationSelector);
    const generalAges: TGeneralAges = useSelector(agesSelector);
    const { totalPrice, totalAdditionalPrice, nonCancelledCartProducts, newOrModifiedCartProducts } =
        useCartPriceCalculation();

    const cartItemsCount = newOrModifiedCartProducts.reduce((acc: number, item: TCartItem) => {
        if (item.count) {
            return acc + item.count;
        }
        return acc + 1;
    }, 0);

    useEffect(() => {
        // Store in localStorage for backwards compatibility
        localStorage.setItem("r360CartItemsCount", cartItemsCount.toString());

        const expiryTimestamp =
            cartItemsCount === 0 && !isReservationModification
                ? ""
                : moment
                      .min(
                          nonCancelledCartProducts
                              .filter((product: TCartItem) => !product.detlid) // Exclude already booked products.
                              .map((product: TCartItem) => moment(product.created))
                      )
                      .add(1, "days")
                      .format("YYYY-MM-DD HH:mm:ss");

        // Store expiry timestamp in localStorage for backwards compatibility
        localStorage.setItem("r360CartExpiryTimestamp", expiryTimestamp);

        // Send signal with updated cart information
        sendSignal(
            signal(SIGNAL_CART_UPDATED, {
                cartProductsCount: cartItemsCount,
                cartExpiryTimestamp: expiryTimestamp,
                cartPrice: isReservationModification ? totalAdditionalPrice : totalPrice,
                cartProducts: newOrModifiedCartProducts
                    .filter(product => !product.connectedProductId)
                    .map(product => {
                        let productAddPrice = 0;

                        if (isBooked(product)) {
                            productAddPrice = product.bookedprice ? product.price - product.bookedprice : 0;
                        } else {
                            productAddPrice = getCartItemPrice(product, generalAges);
                        }

                        return {
                            isBooked: isBooked(product),
                            title: getProductTitle(product),
                            productType: product.kind,
                            guestAges: Object.values(product.guestages),
                            minimumAge: product.minage,
                            maximumAge: product.maxage,
                            addPrice: productAddPrice,
                            addQuantity: isBooked(product) ? 0 : product.count || product.quantity || 0,
                            extras: (product.info?.extras || []).map(extra => ({
                                isBooked: isBooked(extra),
                                title: extra.title,
                                addPrice: isBooked(extra) ? extra.additional?.price || 0 : extra.price,
                                addQuantity: isBooked(extra) ? extra.additional?.quantity || 0 : extra.quantity,
                            })),
                            connectedProducts: nonCancelledCartProducts
                                .filter(connectedProduct => connectedProduct.connectedProductId === product.cartItemId)
                                .map(connectedProduct => ({
                                    isBooked: isBooked(connectedProduct),
                                    title: getProductTitle(connectedProduct),
                                    productType: connectedProduct.kind,
                                    minimumAge: connectedProduct.minage,
                                    maximumAge: connectedProduct.maxage,
                                    addPrice: isBooked(connectedProduct)
                                        ? 0 // Price cannot change after booking for connected products.
                                        : getCartItemPrice(connectedProduct, generalAges),
                                    addQuantity: isBooked(connectedProduct) ? 0 : connectedProduct.count || 0,
                                })),
                        };
                    }),
                cartType: isReservationModification ? "modify" : "new",
            })
        );
    }, [cartItemsCount, totalPrice, totalAdditionalPrice, isReservationModification]);
};
